import type { ContourLabel, ContourLabelItemId } from '@/src/store/contours/contour-definitions';
import type { FrameDataInput } from '@/src/store/contours/contour-processing/contourPropagation';
import type { Point, PointArray } from '@/src/store/contours/types/contour-definition-array-type';

import { useAlert } from '@/src/composables/useAlert';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import {
  ContourDefinitions,
  ContourLabel as ContourLabelEnum,
  ContourType,
} from '@/src/store/contours/contour-definitions';
import {
  computeNormals,
  DEFAULT_CONTOUR_PROPAGATION_OPTIONS,
  propagateContourOneStep,
  resampleArcLength,
  sampleProfile,
  updateRollingProfiles,
} from '@/src/store/contours/contour-processing/contourPropagation';
import { lucasKanadePointPyramidal } from '@/src/store/contours/contour-processing/opticalPointTrace';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

export interface SmartPasteOptions {
  /**
   * If true, propagates and updates contours across all intermediate phases
   * from start phase to target phase (Ctrl+Shift+Alt+V / Ctrl+Shift+A+V).
   * If false or undefined, only pastes the deformed contour on the target phase (Ctrl+Shift+V).
   */
  allPhases?: boolean;
}

/**
 * Performs smart deformation-aware contour pasting from source phase to target phase.
 */
export async function useSmartContourPaste(
  viewerID: string,
  targetImageUID: string,
  options?: SmartPasteOptions,
) {
  const currentStudyStore = useCurrentStudyStore();
  const contourContainerStore = useContourContainerStore();
  const alert = useAlert();

  // 1. Retrieve clipboard contours
  const clipboard = contourContainerStore.getTempCopyContours();
  if (!clipboard || clipboard.length === 0) {
    alert.warning('Cannot paste: Clipboard is empty.');
    return;
  }

  // 2. Resolve target image metadata
  const targetInfo = currentStudyStore.getIndexByImageUID(targetImageUID);
  if (!targetInfo) {
    contourContainerStore.pasteTempCopyContours(targetImageUID);
    return;
  }

  // 3. Resolve cine frame UIDs for target slice
  const sliceImageUIDs = currentStudyStore.getSliceImageUIDs(
    targetInfo.imageSeriesIndex,
    targetInfo.imageSliceIndex,
  );
  if (!sliceImageUIDs || sliceImageUIDs.length === 0) {
    contourContainerStore.pasteTempCopyContours(targetImageUID);
    return;
  }

  const batchContours: Array<{
    UID: string;
    points: PointArray;
    label: ContourLabel;
    id?: ContourLabelItemId;
    replace?: boolean;
    text?: string;
  }> = [];

  let tracedCount = 0;
  let directPasteCount = 0;
  let phasesCount = 0;

  for (const item of clipboard) {
    const srcUID = item.sourceImageUID;
    const sourceInfo = srcUID
      ? currentStudyStore.getIndexByImageUID(srcUID)
      : null;

    const isSameSlice
      = sourceInfo
        && sourceInfo.imageSeriesIndex === targetInfo.imageSeriesIndex
        && sourceInfo.imageSliceIndex === targetInfo.imageSliceIndex;

    // If not on same slice/series or same phase, fallback to simple paste
    if (!isSameSlice || sourceInfo.imageIndex === targetInfo.imageIndex) {
      batchContours.push({
        UID: targetImageUID,
        points: item.points,
        label: item.label,
        id: item.id,
        replace: true,
        text: item.text,
      });
      directPasteCount++;
      continue;
    }

    const startPhaseIndex = sourceInfo.imageIndex;
    const targetPhaseIndex = targetInfo.imageIndex;

    // Build path of phase indices from start to target
    const pathIndices: number[] = [];
    if (startPhaseIndex < targetPhaseIndex) {
      for (let p = startPhaseIndex; p <= targetPhaseIndex; p++) {
        pathIndices.push(p);
      }
    }
    else {
      for (let p = startPhaseIndex; p >= targetPhaseIndex; p--) {
        pathIndices.push(p);
      }
    }

    // Fetch VTK image data for frames along the path
    const frames: FrameDataInput[] = [];
    let fetchFailed = false;
    for (const pIdx of pathIndices) {
      const frameUID = sliceImageUIDs[pIdx];
      const imageMeta = await currentStudyStore.fetchOrGetVtkImageData(frameUID);
      if (!imageMeta) {
        fetchFailed = true;
        break;
      }
      const rawData = imageMeta.getPointData().getScalars().getData();
      const data
        = rawData instanceof Float32Array
          ? rawData
          : Float32Array.from(rawData as ArrayLike<number>);
      const dims = imageMeta.getDimensions();
      frames.push({
        data,
        width: dims[0],
        height: dims[1],
      });
    }

    if (fetchFailed || frames.length !== pathIndices.length) {
      // Fallback to direct paste on target
      batchContours.push({
        UID: targetImageUID,
        points: item.points,
        label: item.label,
        id: item.id,
        replace: true,
        text: item.text,
      });
      directPasteCount++;
      continue;
    }

    phasesCount = Math.max(phasesCount, pathIndices.length);

    // Case A: Polygonal contour (>= 3 points)
    if (item.points && item.points.length >= 3) {
      const isOpen
        = item.label === ContourLabelEnum.SaxLvOpenEpi
          || ContourDefinitions[item.label]?.type === ContourType.PolygonOpenContour;
      const propagationOpts = { ...DEFAULT_CONTOUR_PROPAGATION_OPTIONS };
      const { width, height } = frames[0];
      const refPts = resampleArcLength(
        item.points,
        propagationOpts.referencePointCount,
        isOpen,
      );
      const refNormals = computeNormals(refPts, isOpen);
      const staticAnchorProfiles = refPts.map((pt, i) =>
        sampleProfile(
          frames[0].data,
          width,
          height,
          pt,
          refNormals[i],
          propagationOpts.profileHalfLenPx,
          propagationOpts.profileStepPx,
        ),
      );

      let curPoints = refPts;
      let rollingProfiles: Float32Array[] = staticAnchorProfiles.map(p => new Float32Array(p));

      const stepResults: Array<{ frameIdx: number; uid: string; points: PointArray }> = [];

      for (let s = 0; s < pathIndices.length - 1; s++) {
        const prevFrame = frames[s];
        const nextFrame = frames[s + 1];
        const nextPhaseIdx = pathIndices[s + 1];
        const nextUID = sliceImageUIDs[nextPhaseIdx];

        const { points } = propagateContourOneStep(
          prevFrame.data,
          nextFrame.data,
          width,
          height,
          curPoints,
          staticAnchorProfiles,
          rollingProfiles,
          propagationOpts,
          isOpen,
        );

        curPoints = points;
        if (
          propagationOpts.rollingAnchorWeight > 0
          && propagationOpts.rollingAnchorUpdateRate > 0
        ) {
          rollingProfiles = updateRollingProfiles(
            nextFrame.data,
            width,
            height,
            points,
            rollingProfiles,
            propagationOpts.rollingAnchorUpdateRate,
            propagationOpts,
            isOpen,
          );
        }

        stepResults.push({
          frameIdx: nextPhaseIdx,
          uid: nextUID,
          points: curPoints,
        });
      }

      if (!options?.allPhases) {
        // Target phase only
        const targetResult = stepResults[stepResults.length - 1];
        batchContours.push({
          UID: targetResult.uid,
          points: targetResult.points,
          label: item.label,
          id: item.id,
          replace: true,
          text: item.text,
        });
      }
      else {
        // All intermediate phases up to and including target
        for (const res of stepResults) {
          batchContours.push({
            UID: res.uid,
            points: res.points,
            label: item.label,
            id: item.id,
            replace: true,
            text: item.text,
          });
        }
      }
      tracedCount++;
    }
    // Case B: Point contour (1 point, e.g. RV insertion)
    else if (item.points && item.points.length === 1) {
      let curPt: Point = { ...item.points[0] };
      const pointStepResults: Array<{ frameIdx: number; uid: string; point: Point }> = [];

      for (let s = 0; s < pathIndices.length - 1; s++) {
        const prevFrame = frames[s];
        const nextFrame = frames[s + 1];
        const nextPhaseIdx = pathIndices[s + 1];
        const nextUID = sliceImageUIDs[nextPhaseIdx];

        const grayA = {
          data: prevFrame.data as Float32Array,
          width: prevFrame.width,
          height: prevFrame.height,
        };
        const grayB = {
          data: nextFrame.data as Float32Array,
          width: nextFrame.width,
          height: nextFrame.height,
        };

        const disp = lucasKanadePointPyramidal(grayA, grayB, curPt, {
          windowSize: 11,
          pyramidLevels: 3,
          maxIterationsPerLevel: 10,
          convergenceEpsilon: 0.01,
        });

        curPt = {
          x: Math.max(0, Math.min(prevFrame.width - 1, curPt.x + disp.u)),
          y: Math.max(0, Math.min(prevFrame.height - 1, curPt.y + disp.v)),
        };

        pointStepResults.push({
          frameIdx: nextPhaseIdx,
          uid: nextUID,
          point: { ...curPt },
        });
      }

      if (!options?.allPhases) {
        const last = pointStepResults[pointStepResults.length - 1];
        batchContours.push({
          UID: last.uid,
          points: [last.point],
          label: item.label,
          id: item.id,
          replace: true,
          text: item.text,
        });
      }
      else {
        for (const res of pointStepResults) {
          batchContours.push({
            UID: res.uid,
            points: [res.point],
            label: item.label,
            id: item.id,
            replace: true,
            text: item.text,
          });
        }
      }
      tracedCount++;
    }
    // Case C: Non-traceable / text / simple items
    else {
      batchContours.push({
        UID: targetImageUID,
        points: item.points,
        label: item.label,
        id: item.id,
        replace: true,
        text: item.text,
      });
      directPasteCount++;
    }
  }

  // 4. Save batch to store in a single undoable transaction
  if (batchContours.length > 0) {
    contourContainerStore.addContoursBatch(batchContours);

    if (tracedCount > 0) {
      if (options?.allPhases) {
        alert.success(
          `Smart Paste: Traced ${tracedCount} contour(s) across ${phasesCount} phases.`,
        );
      }
      else {
        alert.success(
          `Smart Paste: Deformed ${tracedCount} contour(s) to target phase.`,
        );
      }
    }
    else if (directPasteCount > 0) {
      alert.info(`Pasted ${directPasteCount} contour(s) to target phase.`);
    }
  }
}
